
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { SubscriptionExpiredBanner } from '@/components/SubscriptionExpiredBanner';
import { SubscriptionAlertBanner } from '@/components/subscription/SubscriptionAlertBanner';
import { AIAssistantChat } from '@/components/AIAssistantChat';
import { QuickBookingModal } from '@/components/admin/QuickBookingModal';
import { SuperAdvancedModal } from '@/components/bookings/SuperAdvancedModal';
import { RealtimeProvider } from '@/contexts/RealtimeContext';
import { supabase } from '@/integrations/supabase/client';
import { useGridSettings } from '@/hooks/useGridSettings';
import { parseLocalDate } from '@/utils/dateHelpers';
import type { BookingWithFinancials } from '@/hooks/useBookingCore';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout = ({ children }: AdminLayoutProps) => {
  const [isQuickBookingOpen, setIsQuickBookingOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<BookingWithFinancials | null>(null);
  const { settings } = useGridSettings();

  const defaultDate = useMemo(() => {
    if (settings.startDate && settings.startDate !== 'today') {
      const d = parseLocalDate(settings.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (d >= today) return d;
    }
    return new Date();
  }, [settings.startDate]);

  // Listen for voice command to open quick booking modal
  useEffect(() => {
    const handleOpenQuickBooking = () => {
      setIsQuickBookingOpen(true);
    };

    window.addEventListener('open-quick-booking', handleOpenQuickBooking);
    return () => {
      window.removeEventListener('open-quick-booking', handleOpenQuickBooking);
    };
  }, []);

  // Listen for open-booking-modal event from AI assistant
  useEffect(() => {
    const handleOpenBookingModal = async (e: Event) => {
      const customEvent = e as CustomEvent<{ bookingId: string }>;
      const bookingId = customEvent.detail?.bookingId;
      if (!bookingId) return;

      console.log('📋 AI requested opening booking:', bookingId);

      try {
        // Fetch booking with financials
        const { data: booking, error } = await supabase
          .from('booking_core')
          .select('*')
          .eq('id', bookingId)
          .single();

        if (error || !booking) {
          console.error('Failed to fetch booking:', error);
          return;
        }

        // Fetch financial data
        const { data: financial } = await supabase
          .from('booking_financial')
          .select('*')
          .eq('booking_id', bookingId)
          .maybeSingle();

        // Fetch payments
        const { data: payments } = await supabase
          .from('booking_payments')
          .select('*')
          .eq('booking_id', bookingId)
          .order('payment_date', { ascending: false });

        // Fetch services
        const { data: services } = await supabase
          .from('booking_services')
          .select('*')
          .eq('booking_id', bookingId);

        // Fetch history
        const { data: history } = await supabase
          .from('booking_history')
          .select('*')
          .eq('booking_id', bookingId)
          .order('changed_at', { ascending: false });

        // Fetch booking guests
        const { data: guests } = await supabase
          .from('booking_guests')
          .select('*')
          .eq('booking_id', bookingId);

        const bookingWithFinancials: BookingWithFinancials = {
          ...booking,
          financial: financial || undefined,
          payments: payments || [],
          services: services || [],
          history: history || [],
          guests: guests || [],
          paidAmount: financial?.paid_amount || 0,
          totalAmount: financial?.total_amount || booking.total_amount,
          paymentPercentage: financial ? Math.round((financial.paid_amount / financial.total_amount) * 100) : 0,
        };

        setSelectedBooking(bookingWithFinancials);
        setIsBookingModalOpen(true);
      } catch (err) {
        console.error('Error opening booking modal:', err);
      }
    };

    window.addEventListener('open-booking-modal', handleOpenBookingModal);
    return () => {
      window.removeEventListener('open-booking-modal', handleOpenBookingModal);
    };
  }, []);

  const handleBookingModalClose = useCallback(() => {
    setIsBookingModalOpen(false);
    setSelectedBooking(null);
  }, []);

  return (
    <RealtimeProvider>
      <DashboardLayout>
        <SubscriptionExpiredBanner />
        <SubscriptionAlertBanner className="mx-4 mt-4" />
        {children}
        <AIAssistantChat />
        
        {/* Quick Booking Modal triggered by voice commands */}
        <QuickBookingModal
          isOpen={isQuickBookingOpen}
          onClose={() => setIsQuickBookingOpen(false)}
          selectedDate={defaultDate}
        />

        {/* SuperAdvancedModal triggered by AI assistant open-booking-modal event */}
        <SuperAdvancedModal
          booking={selectedBooking}
          open={isBookingModalOpen}
          onOpenChange={(open) => { if (!open) handleBookingModalClose(); }}
          onBookingUpdated={() => {}}
        />
      </DashboardLayout>
    </RealtimeProvider>
  );
};
